import { inject, observer } from 'mobx-react';
import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Alert } from 'reactstrap';

import PropTypes from 'prop-types';
import Analytics from '../../../analytics/Analytics';
import useMaxQuantity, {
  getStructureMaxQuantity,
} from '../../../hooks/quantity/useMaxQuantity';
import Product from '../../../models/Product';
import FullProduct from '../../../models/product/FullProduct';
import { modelOf } from '../../../prop-types';
import AccountStore from '../../../store/AccountStore';
import CartStore from '../../../store/CartStore';
import ConfigStore from '../../../store/ConfigStore';
import ProposalStore from '../../../store/ProposalStore';
import RecurringOrderStore from '../../../store/RecurringOrderStore';
import ProductAvailabilityType from '../../../types/ProductAvailabilityType';
import ProductClass from '../../../types/ProductClass';
import RequestState from '../../../types/RequestState';
import {
  calculateNearestProductQuantityWithQuantityStep,
  roundWithPrecision,
} from '../../../util/number';
import LocalApiErrors, {
  LocalApiErrorsFallbackMode,
} from '../../common/LocalApiErrors';
import AddToCart from '../../cart/AddToCart';
import CartIcon from '../../cart/CartIcon';
import SimpleAddToCart from '../../cart/SimpleAddToCart';
import ProductWatcherWidget from '../../product-watcher/ProductWatcherWidget';
import ProductRecurringOrder from '../ProductRecurringOrder';
import ProductSeasonButton from '../ProductSeasonButton';
import {
  getStructureRowId,
  getCartProductQuantities,
} from '../ProductStructureSelector/structureSelectionState';

export { DEFAULT_MAX_QUANTITY } from '../../../hooks/quantity/useMaxQuantity';

const createPayload = (id, qty) => {
  return {
    id,
    qty,
  };
};

const getStructureAnalyticsPrice = (product, withTax) => {
  if (!product) {
    return 0;
  }

  if (typeof product.getPrice === 'function') {
    const price = Number(
      product.getPrice(withTax, product.id || product.products_id)
    );

    if (Number.isFinite(price)) {
      return price;
    }
  }

  if (typeof product.price_info?.getPrice === 'function') {
    const price = Number(product.price_info.getPrice(withTax));

    if (Number.isFinite(price)) {
      return price;
    }
  }

  const plainPrice = withTax
    ? Number(product.price_info?.price?.with_tax)
    : Number(product.price_info?.price?.without_tax);

  if (Number.isFinite(plainPrice)) {
    return plainPrice;
  }

  const analyticsPrice = Number(product.analytics?.totalPrice);

  if (Number.isFinite(analyticsPrice)) {
    return analyticsPrice;
  }

  const finalUnitPrice = Number(product.final_unit_price);

  return Number.isFinite(finalUnitPrice) ? finalUnitPrice : 0;
};

export const getStructureAnalyticsProduct = ({
  parentProduct,
  structureItem,
  selectedProduct,
  withTax,
}) => {
  const productId = selectedProduct.products_id || structureItem.products_id;
  const productName = selectedProduct.name || structureItem.name || productId;
  const productPrice = getStructureAnalyticsPrice(selectedProduct, withTax);
  const canonicalPath =
    parentProduct.canonicalPath || parentProduct.canonical_path || [];
  const variations = Array.isArray(selectedProduct.properties)
    ? selectedProduct.properties.map(
        (property) => `${property.name}: ${property.value}`
      )
    : [];

  return {
    analytics: {
      totalPrice: productPrice,
    },
    canonicalPath,
    canonical_path: canonicalPath,
    class: ProductClass.SINGLE,
    id: productId,
    main_section_id: parentProduct.main_section_id,
    manufacturer: parentProduct.manufacturer,
    manufacturers_name: parentProduct.manufacturers_name,
    model: null,
    multiproduct_title: productName,
    name: productName,
    price_info: {
      getPrice: () => productPrice,
    },
    variations,
    getPrice: () => productPrice,
  };
};

export const getStructureAnalyticsProducts = ({
  product,
  structureProductIds,
  quantity,
  itemListId,
  itemListName,
  position,
  withTax,
}) => {
  if (
    product.class !== ProductClass.STRUCTURE ||
    !product.structure ||
    !Array.isArray(product.structure.products)
  ) {
    return [];
  }

  return product.structure.products.reduce((items, structureItem) => {
    const children = Array.isArray(structureItem.children)
      ? structureItem.children
      : [];
    const selectedProduct =
      children.length > 0
        ? children.find(
            (child) =>
              String(child.products_id) ===
              String(structureProductIds?.[getStructureRowId(structureItem)])
          )
        : structureItem;

    if (!selectedProduct) {
      return items;
    }

    return [
      ...items,
      {
        product: getStructureAnalyticsProduct({
          parentProduct: product,
          structureItem,
          selectedProduct,
          withTax,
        }),
        price: getStructureAnalyticsPrice(selectedProduct, withTax),
        quantity: (Number(structureItem.quantity) || 1) * quantity,
        itemListId,
        itemListName,
        position,
      },
    ];
  }, []);
};

const ProductAddToCart = ({
  accountStore,
  cartStore,
  configStore,
  recurringOrderStore,
  proposalStore,
  analytics,
  product,
  addToCartProduct,
  activeProductId,
  listId,
  listName,
  position,
  quantity,
  hideQuantity,
  ifWebStore,
  isValid,
  productPageContent,
  simple,
  withQuantityPicker,
  withRecurringOrder,
  withServices,
  onAddToCheckoutCartClick,
  onUpdateProductQuantity,
  scrollToServices,
  productListView,
  structureProductIds,
}) => {
  const [loading, setLoading] = useState(false);
  const [addToCartError, setAddToCartError] = useState(null);
  const [messageOK, setMessageOK] = useState(null);
  const cartProductQuantities = getCartProductQuantities(
    cartStore?.cart?.products
  );
  const [getMaxQuantity] = useMaxQuantity({
    accountStore,
    configStore,
    addToCartProduct,
    structureProduct: product,
    structureProductIds,
    cartProductQuantities,
  });
  const structureMaxQuantity =
    product.class === ProductClass.STRUCTURE
      ? getStructureMaxQuantity({
          structureProduct: product,
          structureProductIds,
          cartProductQuantities,
        })
      : null;
  const exceedsStructureStock =
    structureMaxQuantity !== null &&
    Number(quantity || 1) > Number(structureMaxQuantity);

  const withTax = accountStore.showPricesWithTax;
  if (product.season && !product.season.hasStarted()) {
    return <ProductSeasonButton season={product.season} />;
  }

  const ifShoppingCenter = configStore.siteConfig.isShoppingCenter;
  const ifBuyingAllowed =
    product.merchantInfo && product.merchantInfo.buyingAllowed;

  if (ifShoppingCenter && !ifBuyingAllowed) {
    return null;
  }

  if (activeProductId && product.isTemporarilyUnavailable(activeProductId)) {
    let productNameWithModel =
      addToCartProduct.name + ' ' + addToCartProduct.model;
    if (product.class === ProductClass.COLLECTION) {
      const collectionItem =
        product.collection.getItemWithProductId(activeProductId);
      if (collectionItem && collectionItem.name) {
        productNameWithModel =
          productNameWithModel + ' – ' + collectionItem.name;
      }
    }
    return !simple ? (
      <ProductWatcherWidget
        price={product.getPrice(withTax)}
        productName={productNameWithModel}
        activeProductId={activeProductId}
      />
    ) : null;
  }

  const onlyInShop =
    addToCartProduct.availability_type === ProductAvailabilityType.ONLY_IN_SHOP;

  const soldOut =
    activeProductId &&
    addToCartProduct.availability_type === ProductAvailabilityType.CLOSEOUT &&
    !product.hasEnoughStockAvailable(activeProductId);

  const hideAddToCart =
    onlyInShop ||
    (product.season && product.season.hasEnded()) ||
    (soldOut &&
      // Don't hide for collection or multi products to avoid confusion caused
      // by arriving to the page and not seeing an add to cart button.
      product.class !== ProductClass.COLLECTION &&
      product.class !== ProductClass.MULTI);

  if (hideAddToCart) {
    return null;
  }

  const disabled =
    !activeProductId || soldOut || !isValid || exceedsStructureStock;
  // Keep quantity editable when structure/selection validity fails so the
  // user can lower quantity (or fix selections) without the field locking.
  const quantityDisabled = !activeProductId || soldOut;

  let disabledMessage;
  if (!activeProductId) {
    disabledMessage = (
      <FormattedMessage
        id="product.selectProductFirst"
        defaultMessage="First choose the product."
      />
    );
  } else if (
    (product.class !== ProductClass.COLLECTION ||
      product.class !== ProductClass.MULTI) &&
    soldOut
  ) {
    disabledMessage = (
      <FormattedMessage
        id="product.currentProductSoldOut"
        defaultMessage="The selected product has been sold out."
      />
    );
  } else if (soldOut) {
    disabledMessage = (
      <FormattedMessage
        id="product.hasBeenSoldOut"
        defaultMessage="This product has been sold out."
      />
    );
  } else if (exceedsStructureStock) {
    disabledMessage = (
      <FormattedMessage
        id="product.structure.stock_validation"
        defaultMessage="Selected items do not have enough stock for this quantity."
      />
    );
  } else if (!isValid) {
    disabledMessage = (
      <FormattedMessage
        id="product.checkingShippingValidity"
        defaultMessage="Product validity for the cart."
      />
    );
  }

  let tooltipMessage;
  const updateInProgress = cartStore.state === RequestState.LOADING;
  if (updateInProgress) {
    tooltipMessage = (
      <FormattedMessage
        id="cart.beingUpdated"
        defaultMessage="Cart is being updated"
      />
    );
  }

  let quantityAllowed =
    accountStore.isRetailer || configStore.productCard.showQuantitySelection;
  if (hideQuantity) {
    quantityAllowed = false;
  }

  const quantityStep = addToCartProduct.getQuantityStep(product);
  const buyingAllowed = !(
    configStore.recurringOrder.enabled &&
    accountStore.loggedIn &&
    addToCartProduct.hasOnlyRecurringOrder()
  );

  const handleAddToProposal = (quantityValue) => {
    if (proposalStore.state === RequestState.LOADING) {
      return;
    }

    if (activeProductId) {
      const quantityStep = addToCartProduct.getQuantityStep(product);
      const newQuantity = calculateNearestProductQuantityWithQuantityStep(
        quantityValue,
        quantityStep
      );
      const value = product.getPrice(withTax, activeProductId);

      proposalStore
        .addToProposal(createPayload(activeProductId, newQuantity))
        .then(() => {
          setLoading(false);
          setAddToCartError(null);
          setMessageOK(null);

          analytics.addToProposal(
            [
              {
                product,
                quantity: newQuantity,
                activeProductId,
                position,
                itemListName: listName,
                itemListId: listId,
              },
            ],
            roundWithPrecision(value * newQuantity, 4)
          );
        })
        .catch((e) => {
          setLoading(false);
          setAddToCartError(e);
          setMessageOK(null);
        });
    }
  };

  const handleAddToCartClick = (
    quantityValue,
    services = [],
    newRecurring = {}
  ) => {
    if (onAddToCheckoutCartClick) {
      onAddToCheckoutCartClick(addToCartProduct);
      return;
    }
    // If cartStore is already loading, don't do anything
    if (cartStore.state === RequestState.LOADING) {
      return;
    }

    if (activeProductId) {
      const quantityStep = addToCartProduct.getQuantityStep(product);
      const newQuantity = calculateNearestProductQuantityWithQuantityStep(
        quantityValue,
        quantityStep
      );
      const value = product.getPrice(withTax, activeProductId);
      const withActiveCategory = productListView !== true;
      setLoading(true);

      cartStore
        .addToCart(
          activeProductId,
          newQuantity,
          [],
          services,
          newRecurring,
          withActiveCategory,
          structureProductIds
        )
        .then(() => {
          setLoading(false);
          setAddToCartError(null);
          setMessageOK(null);

          analytics.addToCart(
            getAnalyticsProducts(newQuantity),
            roundWithPrecision(value * newQuantity * quantityStep, 4)
          );

          analytics.cartStatus({
            cart: cartStore.cart,
          });
        })
        .catch((e) => {
          setLoading(false);
          setAddToCartError(e);
          setMessageOK(null);
        });
    }
  };

  const handleAddToOrderClick = (orderId, quantityValue, cycle) => {
    // If cartStore is already loading, don't do anything
    if (recurringOrderStore.state === RequestState.LOADING) {
      return;
    }
    if (activeProductId) {
      const quantityStep = addToCartProduct.getQuantityStep(product);
      const newQuantity = calculateNearestProductQuantityWithQuantityStep(
        quantityValue,
        quantityStep
      );
      const value = product.getPrice(withTax, activeProductId);

      setLoading(true);

      const orderProduct = Object.assign(
        {
          quantity: newQuantity,
          extendedId: activeProductId,
        },
        cycle
      );

      recurringOrderStore
        .addProduct(orderId, orderProduct)
        .then(() => {
          setLoading(false);
          setMessageOK(true);

          analytics.addToCart(
            getAnalyticsProducts(newQuantity),
            roundWithPrecision(value * newQuantity * quantityStep, 4)
          );
          analytics.cartStatus({
            cart: cartStore.cart,
          });
        })
        .catch((e) => {
          setLoading(false);
          setMessageOK(false);
        });
    }
  };

  const handleRecurringToCart = (newRecurring) => {
    handleAddToCartClick(quantity, [], newRecurring);
  };

  const handleRecurringToOrder = (orderId, newRecurring) => {
    handleAddToOrderClick(orderId, quantity, newRecurring);
  };

  const updateQuantity = (quantity) => {
    if (onUpdateProductQuantity) {
      onUpdateProductQuantity(quantity);
    }
  };

  const getAnalyticsProducts = (selectedQuantity) => {
    const baseProduct = {
      product,
      quantity: selectedQuantity,
      activeProductId,
      position,
      itemListName: listName,
      itemListId: listId,
    };
    const structureProducts = getStructureAnalyticsProducts({
      product,
      structureProductIds,
      quantity: selectedQuantity,
      itemListId: listId,
      itemListName: listName,
      position,
      withTax,
    });

    return structureProducts.length > 0
      ? [baseProduct, ...structureProducts]
      : [baseProduct];
  };

  const renderOrderMessage = () => {
    return messageOK ? (
      <Alert className="ProductAddToCart__errors" color="success">
        <FormattedMessage
          id="order.productAddedSentence"
          defaultMessage="Product added to order"
        />
      </Alert>
    ) : (
      <Alert className="ProductAddToCart__errors" color="danger">
        <FormattedMessage
          id="order.productAddFailedSentence"
          defaultMessage="Product cannot be added for order"
        />
      </Alert>
    );
  };

  let content = null;

  if (!simple) {
    content = (
      <>
        <div className="ProductAddToCart ProductAddToCart--normal">
          {buyingAllowed && (
            <AddToCart
              key={addToCartProduct.id}
              activeProductId={activeProductId}
              notAllowed={updateInProgress}
              disabled={disabled}
              quantityDisabled={quantityDisabled}
              tooltipMessage={disabledMessage || tooltipMessage}
              onAddToCartClick={handleAddToCartClick}
              onAddToProposalClick={handleAddToProposal}
              maxQuantity={getMaxQuantity()}
              quantityStep={quantityStep}
              unit={addToCartProduct.stock_unit}
              loading={loading}
              withTax={withTax}
              product={product}
              onUpdateProductQuantity={updateQuantity}
              quantity={quantity}
              isProposal={configStore.siteConfig.isOnlyProposal}
              withServices={withServices}
              withQuantityPicker={withQuantityPicker}
              scrollToServices={scrollToServices}
              productPageContent={productPageContent}
            />
          )}
        </div>
        {configStore.siteConfig.isHybrid && (
          <div className="ProductAddToCart ProductAddToCart--proposal">
            <AddToCart
              key={addToCartProduct.id}
              activeProductId={activeProductId}
              notAllowed={updateInProgress}
              disabled={disabled}
              quantityDisabled={quantityDisabled}
              tooltipMessage={disabledMessage || tooltipMessage}
              onAddToCartClick={handleAddToCartClick}
              onAddToProposalClick={handleAddToProposal}
              maxQuantity={getMaxQuantity()}
              quantityStep={quantityStep}
              unit={addToCartProduct.stock_unit}
              loading={loading}
              withTax={withTax}
              product={product}
              onUpdateProductQuantity={updateQuantity}
              quantity={quantity}
              isProposal={true}
              withQuantityPicker={withQuantityPicker}
              withServices={false}
              scrollToServices={scrollToServices}
              productPageContent={productPageContent}
            />
          </div>
        )}
        <LocalApiErrors
          className="ProductAddToCart__errors"
          error={addToCartError}
          fallbackMode={LocalApiErrorsFallbackMode.LOCAL_ONLY}
        />
        {withRecurringOrder && (
          <ProductRecurringOrder
            product={product}
            withTax={withTax}
            onAddToCartClick={handleRecurringToCart}
            onAddToOrderClick={handleRecurringToOrder}
            disabled={disabled}
            loading={loading}
          />
        )}
        {withRecurringOrder && messageOK !== null && renderOrderMessage()}
      </>
    );
  } else if (simple && !disabled && buyingAllowed) {
    content = (
      <>
        {quantityAllowed ? (
          <SimpleAddToCart
            notAllowed={updateInProgress}
            loading={loading}
            maxQuantity={getMaxQuantity()}
            quantityStep={quantityStep}
            onAddToCartClick={handleAddToCartClick}
            onAddToProposalClick={handleAddToProposal}
            unit={addToCartProduct.stock_unit}
            tooltipMessage={disabledMessage || tooltipMessage}
            disabled={disabled}
            onUpdateProductQuantity={updateQuantity}
            quantity={quantity}
            ifWebStore={ifWebStore}
          />
        ) : (
          ifWebStore && (
            <CartIcon
              notAllowed={updateInProgress}
              onClick={() => handleAddToCartClick(quantityStep)}
              onProposalClick={() => handleAddToProposal(quantityStep)}
              loading={loading}
              tooltipMessage={tooltipMessage}
            />
          )
        )}
        <LocalApiErrors
          className="ProductAddToCart__errors"
          error={addToCartError}
          fallbackMode={LocalApiErrorsFallbackMode.LOCAL_ONLY}
        />
      </>
    );
  }

  return content;
};

ProductAddToCart.propTypes = {
  accountStore: modelOf(AccountStore).isRequired,
  cartStore: modelOf(CartStore).isRequired,
  configStore: modelOf(ConfigStore).isRequired,
  recurringOrderStore: modelOf(RecurringOrderStore).isRequired,
  proposalStore: modelOf(ProposalStore).isRequired,
  analytics: PropTypes.instanceOf(Analytics).isRequired,
  product: PropTypes.oneOfType([
    modelOf(Product).isRequired,
    modelOf(FullProduct).isRequired,
  ]),
  addToCartProduct: PropTypes.object.isRequired,
  activeProductId: PropTypes.string,
  listId: PropTypes.string,
  listName: PropTypes.string,
  position: PropTypes.number,
  quantity: PropTypes.number,
  hideQuantity: PropTypes.bool,
  ifWebStore: PropTypes.bool,
  isValid: PropTypes.bool,
  productPageContent: PropTypes.bool,
  simple: PropTypes.bool,
  withQuantityPicker: PropTypes.bool,
  withRecurringOrder: PropTypes.bool,
  withServices: PropTypes.bool,
  onAddToCheckoutCartClick: PropTypes.func,
  onUpdateProductQuantity: PropTypes.func,
  scrollToServices: PropTypes.func,
  structureProductIds: PropTypes.object,
};

ProductAddToCart.defaultProps = {
  simple: false,
  hideQuantity: false,
  ifWebStore: true,
  withQuantityPicker: true,
  isValid: true,
};

export default inject(
  'accountStore',
  'cartStore',
  'configStore',
  'recurringOrderStore',
  'analytics',
  'recurringOrderStore',
  'proposalStore'
)(observer(ProductAddToCart));
